import type * as React from "react";
import { Box, Typography } from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import {
  standaloneSurfaceCardSx
} from "components/v3/layouts/StandaloneSurveyPageLayout";
import GLButton from "components/v3/sharedComponents/GLButton";
import { COLORS } from "components/v3/Theme/colors";
import { PATHS } from "constants/pathConstants";
import HeaderLogo from "assets/images/GL-Logo.svg";
import PageTitle from "utils/PageTitle";
import Translate from "translate/Translate";

type StandaloneConfirmationPageLayoutProps = {
  backLabel?: string;
  children: React.ReactNode;
  onBack?: () => void;
  onPrimary: () => void;
  pageTitle: string;
  primaryLabel: string;
  title: string;
};

const StandaloneConfirmationPageLayout: React.FC<
  StandaloneConfirmationPageLayoutProps
> = ({
  backLabel,
  children,
  onBack,
  onPrimary,
  pageTitle,
  primaryLabel,
  title
}) => {
  const history = useHistory();

  PageTitle(pageTitle);

  return (
    <Box
      sx={{
        background:
          "linear-gradient(180deg, #F2F7FB 0%, rgba(242, 247, 251, 0.00) 62.6%)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
        overflow: "auto",
        pb: "40px",
        position: "relative"
      }}
    >
      <Box
        onClick={() => history.push(PATHS.LANDING_PAGE)}
        sx={{
          alignItems: "center",
          cursor: "pointer",
          display: "flex",
          gap: { lg: "12px", xs: "4px" },
          left: { lg: "64px", sm: "28px", xs: "12px" },
          position: "absolute",
          top: "38px",
          width: "fit-content",
          zIndex: 1
        }}
      >
        <ArrowBack sx={{ color: COLORS.DARK_GRAY, height: 20, width: 20 }} />
        <Typography variant="body_strong_dark_gray">
          <Translate text="Exit" />
        </Typography>
      </Box>

      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexShrink: 0,
          justifyContent: "center",
          py: 3
        }}
      >
        <Box
          alt="Cancer Guardian by Genomic Life logo"
          component="img"
          src={HeaderLogo}
          sx={{ maxHeight: "41px", maxWidth: "175px" }}
        />
      </Box>

      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          justifyContent: "center",
          marginX: "auto",
          maxWidth: 830,
          px: { lg: 0, sm: 4, xs: 2 },
          py: { md: 4, xs: 2 },
          width: "100%"
        }}
      >
        <Box
          sx={{
            ...standaloneSurfaceCardSx,
            borderRadius: "12px",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            px: { md: 4, xs: 2.5 },
            py: { md: "62px", xs: "50px" },
            width: "100%"
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "26px" }}>
            <Typography
              component="h1"
              sx={{ m: 0 }}
              variant="body_strong_lg_black"
            >
              <Translate text={title} />
            </Typography>
            {children}
          </Box>

          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexDirection: { sm: "row", xs: "column-reverse" },
              gap: 2,
              justifyContent: onBack ? "space-between" : "flex-end"
            }}
          >
            {onBack && (
              <GLButton
                buttonType="secondary-no-border"
                onClick={onBack}
                sx={{
                  justifyContent: "flex-start",
                  minWidth: 0,
                  paddingLeft: 0,
                  typography: "body_strong_dark_gray",
                  "&:hover:not(.Mui-disabled)": {
                    backgroundColor: "transparent"
                  }
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: `${COLORS.DARK_GRAY} !important`,
                    typography: "body_strong_dark_gray"
                  }}
                >
                  <Translate text={backLabel || "Back"} />
                </Typography>
              </GLButton>
            )}
            <GLButton onClick={onPrimary} rightIcon={<ArrowForward />}>
              <Typography variant="body_strong_white">
                <Translate text={primaryLabel} />
              </Typography>
            </GLButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default StandaloneConfirmationPageLayout;
